import {
  AlertTriangle,
  Crown,
  Gamepad2,
  LoaderCircle,
  Medal,
  RefreshCw,
  Trophy,
  UserRound,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { formatScore } from "../data/leaderboard";
import {
  getLeaderboardRepository,
  type LeaderboardEntry,
  type LeaderboardRepository,
  type OwnScoreSummary,
} from "../data/leaderboardRepository";

type LoadState = "loading" | "ready" | "empty" | "error" | "unconfigured";

function podiumEntries(entries: LeaderboardEntry[]) {
  const firstThree = entries.slice(0, 3);
  return [2, 1, 3]
    .map((rank) => firstThree.find((entry) => entry.rank === rank))
    .filter((entry): entry is LeaderboardEntry => Boolean(entry));
}

function statusLabel(state: LoadState) {
  if (state === "loading") return "Toplista frissítése";
  if (state === "ready") return "Élő Firebase-adatok";
  if (state === "empty") return "Még nincs eredmény";
  if (state === "error") return "Betöltési hiba";
  return "Firebase nincs konfigurálva";
}

export function LeaderboardPage({
  repository,
}: {
  repository?: LeaderboardRepository | null;
}) {
  const auth = useAuth();
  const resolvedRepository = useMemo(
    () => (repository === undefined ? getLeaderboardRepository() : repository),
    [repository],
  );
  const [reloadKey, setReloadKey] = useState(0);
  const [loadState, setLoadState] = useState<LoadState>(
    resolvedRepository ? "loading" : "unconfigured",
  );
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [ownScore, setOwnScore] = useState<OwnScoreSummary | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [ownScoreError, setOwnScoreError] = useState("");

  useEffect(() => {
    let active = true;
    if (!resolvedRepository) {
      setLoadState("unconfigured");
      setEntries([]);
      setOwnScore(null);
      return () => {
        active = false;
      };
    }

    setLoadState("loading");
    setErrorMessage("");
    setOwnScoreError("");

    const ownScorePromise = auth.account
      ? resolvedRepository.loadOwnScore(auth.account.uid).catch((error: unknown) => {
          if (active) {
            setOwnScoreError(
              error instanceof Error ? error.message : "A saját eredmény nem tölthető be.",
            );
          }
          return null;
        })
      : Promise.resolve(null);

    void Promise.all([resolvedRepository.loadTopTen(), ownScorePromise])
      .then(([nextEntries, nextOwnScore]) => {
        if (!active) return;
        setEntries(nextEntries);
        setOwnScore(nextOwnScore);
        setLoadState(nextEntries.length > 0 ? "ready" : "empty");
      })
      .catch((error: unknown) => {
        if (!active) return;
        setEntries([]);
        setOwnScore(null);
        setErrorMessage(
          error instanceof Error ? error.message : "A toplistát most nem sikerült betölteni.",
        );
        setLoadState("error");
      });

    return () => {
      active = false;
    };
  }, [auth.account?.uid, reloadKey, resolvedRepository]);

  const podium = podiumEntries(entries);
  const remainingEntries = entries.slice(3);

  return (
    <div className="page page--leaderboard">
      <section className="page-intro content-frame">
        <p className="eyebrow"><Trophy aria-hidden="true" size={16} /> Összesített rangsor</p>
        <h1>Top 10 játékos</h1>
        <p>A játékosok legjobb eredményei, pontszám szerint csökkenő sorrendben.</p>
        <span className="preview-label">{statusLabel(loadState)}</span>
      </section>

      {loadState === "loading" && (
        <section className="leaderboard-state content-frame" role="status">
          <LoaderCircle className="spin" aria-hidden="true" size={30} />
          <h2>Toplista betöltése</h2>
          <p>Összegyűjtjük a tíz legjobb eredményt.</p>
        </section>
      )}

      {loadState === "unconfigured" && (
        <section className="leaderboard-state content-frame" role="status">
          <AlertTriangle aria-hidden="true" size={30} />
          <h2>A toplista ebben a buildben nem érhető el</h2>
          <p>A játékhoz még nincs Firebase adatkapcsolat konfigurálva.</p>
        </section>
      )}

      {loadState === "error" && (
        <section className="leaderboard-state content-frame" role="alert">
          <AlertTriangle aria-hidden="true" size={30} />
          <h2>A toplista nem töltődött be</h2>
          <p>{errorMessage}</p>
          <button
            className="button button--secondary"
            type="button"
            onClick={() => setReloadKey((current) => current + 1)}
          >
            <RefreshCw aria-hidden="true" size={18} /> Újrapróbálás
          </button>
        </section>
      )}

      {loadState === "empty" && (
        <section className="leaderboard-state content-frame">
          <Trophy aria-hidden="true" size={34} />
          <h2>Még nincs toplistaeredmény</h2>
          <p>Fejezd be az első játékot, és foglald el az első helyet.</p>
          <Link className="button button--primary" to="/play">
            <Gamepad2 aria-hidden="true" size={19} /> Játék indítása
          </Link>
        </section>
      )}

      {loadState === "ready" && (
        <>
          <section className="podium-band" aria-label="Az első három helyezett">
            <div className={`content-frame podium podium--${podium.length}`}>
              {podium.map((entry) => (
                <article className={`podium__place podium__place--${entry.rank}`} key={entry.id}>
                  <div className="podium__avatar" aria-hidden="true">
                    {entry.rank === 1 ? <Crown size={31} /> : <Medal size={29} />}
                  </div>
                  <span className="podium__rank">{entry.rank}.</span>
                  <h2>{entry.alias}</h2>
                  <span className={`player-badge player-badge--${entry.playerType}`}>
                    {entry.playerType === "guest" ? "Vendég" : "Játékos"}
                  </span>
                  <strong>{formatScore(entry.score)}</strong>
                  <small>pont</small>
                </article>
              ))}
            </div>
          </section>

          {remainingEntries.length > 0 && (
            <section className="section content-frame" aria-labelledby="ranking-title">
              <div className="section-heading section-heading--compact">
                <h2 id="ranking-title">4-10. helyezett</h2>
                <span className="section-index">Legjobb eredmény</span>
              </div>
              <ol className="ranking-list" start={4}>
                {remainingEntries.map((entry) => (
                  <li key={entry.id}>
                    <span className="ranking-list__rank">{entry.rank}</span>
                    <span className="ranking-list__player">
                      <UserRound aria-hidden="true" size={19} />
                      <span>
                        <strong>{entry.alias}</strong>
                        <small>{entry.playerType === "guest" ? "Vendég" : "Játékos"}</small>
                      </span>
                    </span>
                    <span className="ranking-list__score">
                      {formatScore(entry.score)} <small>pont</small>
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </>
      )}

      <section className="own-score-band" aria-labelledby="own-score-title">
        <div className="content-frame own-score-band__inner">
          <div>
            <p className="section-kicker">Saját eredmény</p>
            {!auth.account ? (
              <>
                <h2 id="own-score-title">Játssz vendégként vagy Google-fiókkal</h2>
                <p>Belépés után itt jelenik meg a legjobb és a legutóbbi pontszámod.</p>
              </>
            ) : loadState === "loading" ? (
              <>
                <h2 id="own-score-title">Saját eredmény betöltése</h2>
                <p>Megkeressük a legjobb és a legutóbbi pontszámodat.</p>
              </>
            ) : ownScoreError ? (
              <>
                <h2 id="own-score-title">A saját eredmény nem tölthető be</h2>
                <p>{ownScoreError}</p>
              </>
            ) : ownScore?.bestScore !== null && ownScore?.bestScore !== undefined ? (
              <>
                <h2 id="own-score-title">Legjobb: {formatScore(ownScore.bestScore)} pont</h2>
                <p>
                  Legutóbbi: {formatScore(ownScore.latestScore ?? ownScore.bestScore)} pont
                  {auth.account.publicNickname ? ` · ${auth.account.publicNickname}` : ""}
                </p>
              </>
            ) : (
              <>
                <h2 id="own-score-title">Még nincs mentett játékod</h2>
                <p>Fejezz be egy játékot, és az eredményed itt is megjelenik.</p>
              </>
            )}
          </div>
          <Link className="button button--primary" to="/play">
            <Gamepad2 aria-hidden="true" size={20} /> Játék indítása
          </Link>
        </div>
      </section>
    </div>
  );
}
